/**
 * Outbound-click placements: where on the site a `/go/{slug}` click came from.
 * Stored in `directory_click.placement`; developer click reports and trend
 * slot pricing group by it, so every value must come from these builders.
 *
 * Shape: `<surface>[:<segment>[:<segment>]]`, each part a lowercase slug.
 *   trend:<trend-slug>:result   — slot under a finished render (client-rendered)
 *   trend:<trend-slug>:section  — SSR slot block on the trend page
 *   home:<area>                 — homepage, e.g. home:board, home:ad
 *   category:<sub-category>     — /sub-category/<slug> listings
 *   search                      — /search results
 *   item:<slug>                 — item page visit button
 */

export const PLACEMENT_SURFACES = ['trend', 'home', 'category', 'search', 'item'] as const;
export type PlacementSurface = (typeof PLACEMENT_SURFACES)[number];

/** The two trend-page slot positions (docs/roadmaps/aigclist-trends-app-store-plan.md §4.5). */
export type TrendSlotPosition = 'result' | 'section';

/** Query parameter `/go/{slug}` reads the placement from. */
export const PLACEMENT_PARAM = 'p';

const SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_PARTS = 3;
const MAX_LENGTH = 120;

/**
 * Validates an untrusted placement (the `/go` query string). Returns the
 * placement unchanged when well-formed, otherwise null — junk never reaches
 * the analytics table.
 */
export function parsePlacement(raw: string | null | undefined): string | null {
  if (!raw || raw.length > MAX_LENGTH) return null;
  const parts = raw.split(':');
  if (parts.length > MAX_PARTS) return null;
  if (!(PLACEMENT_SURFACES as readonly string[]).includes(parts[0]!)) return null;
  return parts.every((part) => SEGMENT.test(part)) ? raw : null;
}

export function trendPlacement(trendSlug: string, position: TrendSlotPosition): string {
  return `trend:${trendSlug}:${position}`;
}

/** Outbound link through the click logger; invalid placements are dropped, not sent. */
export function goHref(slug: string, placement?: string | null): string {
  const base = `/go/${encodeURIComponent(slug)}`;
  const valid = parsePlacement(placement);
  return valid ? `${base}?${PLACEMENT_PARAM}=${encodeURIComponent(valid)}` : base;
}
